import { API_BASE } from '../../lib/config';
/**
 * TeamLogoUpload - Component for uploading team logo
 * 
 * Features:
 * - Only Admin/Head Coach can upload
 * - Drag and drop support
 * - Preview before upload
 * - Max 5 MB, PNG/JPG/WebP/SVG
 * - Auto-resize to 512x512 on server
 * - URL input as fallback
 */
import React, { useRef, useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { Camera, Trash2, Loader2, Upload, Image, Link2, X } from 'lucide-react';
import { Button } from '../ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Input } from '../ui/input';
import { toast } from 'sonner';
import { cn } from '../../lib/utils';

const API = API_BASE;

export default function TeamLogoUpload({ team, onUpdate, canEdit = false }) {
  const { t } = useTranslation('app_admin');
  const fileInputRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  const [isDragOver, setIsDragOver] = useState(false);
  const [showUrlInput, setShowUrlInput] = useState(false);
  const [urlInput, setUrlInput] = useState('');

  const handleUpload = async (file) => {
    if (!file) return;

    // Validate file type
    if (!file.type.match(/^image\/(jpeg|png|webp|svg\+xml)$/)) {
      toast.error(t('logo_ext.toast_bad_type'));
      return;
    }

    if (file.size > 5 * 1024 * 1024) {
      toast.error(t('logo_ext.toast_too_big'));
      return;
    }

    try {
      setUploading(true);
      const formData = new FormData();
      formData.append('file', file);

      const token = localStorage.getItem('token');
      const response = await axios.post(`${API}/api/media/teams/${team.id}/logo`, formData, {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'multipart/form-data'
        }
      });

      toast.success(t('logo_ext.toast_uploaded'));
      if (onUpdate) onUpdate();
    } catch (error) {
      console.error('Error uploading logo:', error);
      toast.error(error.response?.data?.detail || t('logo_ext.toast_upload_failed'));
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const handleUrlSubmit = async () => {
    if (!urlInput.trim()) return;
    
    // Validate URL format
    try {
      new URL(urlInput);
    } catch {
      toast.error(t('logo_ext.toast_bad_url'));
      return;
    }

    try {
      setUploading(true);
      const token = localStorage.getItem('token');
      
      await axios.patch(`${API}/api/teams/${team.id}`, {
        logo_url: urlInput.trim()
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });

      toast.success(t('logo_ext.toast_url_updated'));
      setShowUrlInput(false);
      setUrlInput('');
      if (onUpdate) onUpdate();
    } catch (error) {
      console.error('Error setting logo URL:', error);
      toast.error(error.response?.data?.detail || t('logo_ext.toast_url_failed'));
    } finally {
      setUploading(false);
    }
  };

  const handleDelete = async () => {
    if (!team?.logo_url) return;

    try {
      setUploading(true);
      const token = localStorage.getItem('token');
      await axios.delete(`${API}/api/media/teams/${team.id}/logo`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      toast.success(t('logo_ext.toast_removed'));
      if (onUpdate) onUpdate();
    } catch (error) {
      console.error('Error deleting logo:', error);
      toast.error(t('logo_ext.toast_delete_failed'));
    } finally {
      setUploading(false);
    }
  };

  const handleFileChange = (e) => {
    const file = e.target.files?.[0];
    if (file) handleUpload(file);
  };

  const handleDrop = useCallback((e) => {
    e.preventDefault();
    setIsDragOver(false);
    
    if (!canEdit) return;
    
    const file = e.dataTransfer.files?.[0];
    if (file) handleUpload(file);
  }, [canEdit]);

  const handleDragOver = useCallback((e) => {
    e.preventDefault();
    if (canEdit) setIsDragOver(true);
  }, [canEdit]);

  const handleDragLeave = useCallback(() => {
    setIsDragOver(false);
  }, []);

  // Get logo URL - handle both relative and absolute URLs
  const getLogoSrc = () => {
    if (!team?.logo_url) return null;
    if (team.logo_url.startsWith('http')) return team.logo_url;
    return `${API}${team.logo_url}`;
  };

  if (!canEdit && !team?.logo_url) {
    return null; // Don't show anything if no permission and no logo
  }

  return (
    <Card className="bg-card border-border">
      <CardHeader className="pb-3">
        <CardTitle className="text-sm font-medium flex items-center gap-2">
          <Image className="w-4 h-4" />
          {t('logo_ext.logo')}
        </CardTitle>
      </CardHeader>
      <CardContent>
        {/* URL Input Mode */}
        {showUrlInput ? (
          <div className="space-y-3">
            <div className="flex gap-2">
              <Input
                value={urlInput}
                onChange={(e) => setUrlInput(e.target.value)}
                placeholder={t('logo_ext.url_mode_url_ph')}
                className="flex-1"
              />
              <Button
                size="sm"
                onClick={handleUrlSubmit}
                disabled={uploading || !urlInput.trim()}
              >
                {uploading ? <Loader2 className="w-4 h-4 animate-spin" /> : t('logo_ext.save')}
              </Button>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => {
                  setShowUrlInput(false);
                  setUrlInput('');
                }}
              >
                <X className="w-4 h-4" />
              </Button>
            </div>
            <p className="text-xs text-muted-foreground">
              {t('logo_ext.url_mode_hint')}
            </p>
          </div>
        ) : (
          <>
            <div
              className={cn(
                "relative flex flex-col items-center gap-4 p-4 rounded-lg border-2 border-dashed transition-colors",
                isDragOver && canEdit ? "border-primary bg-primary/5" : "border-border",
                canEdit && "cursor-pointer hover:border-primary/50"
              )}
              onDrop={handleDrop}
              onDragOver={handleDragOver}
              onDragLeave={handleDragLeave}
              onClick={() => canEdit && fileInputRef.current?.click()}
            >
              {/* Current logo or placeholder */}
              {team?.logo_url ? (
                <div className="relative group">
                  <img
                    src={getLogoSrc()}
                    alt={team.name}
                    className="w-24 h-24 rounded-lg object-cover border border-border"
                    onError={(e) => {
                      e.target.style.display = 'none';
                      e.target.nextSibling.style.display = 'flex';
                    }}
                  />
                  <div className="w-24 h-24 rounded-lg bg-muted/30 items-center justify-center border border-dashed border-border hidden">
                    <span className="text-xs text-muted-foreground">{t('logo_ext.load_failed_alt')}</span>
                  </div>
                  {canEdit && (
                    <div className="absolute inset-0 bg-black/60 rounded-lg opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                      {uploading ? (
                        <Loader2 className="w-6 h-6 text-[rgb(var(--tn-text-ffffff))] animate-spin" />
                      ) : (
                        <Camera className="w-6 h-6 text-[rgb(var(--tn-text-ffffff))]" />
                      )}
                    </div>
                  )}
                </div>
              ) : (
                <div className="w-24 h-24 rounded-lg bg-muted/30 flex flex-col items-center justify-center border border-dashed border-border">
                  {uploading ? (
                    <Loader2 className="w-8 h-8 text-muted-foreground animate-spin" />
                  ) : (
                    <>
                      <Upload className="w-8 h-8 text-muted-foreground mb-2" />
                      <span className="text-xs text-muted-foreground text-center px-2">
                        {t('logo_ext.none')}
                      </span>
                    </>
                  )}
                </div>
              )}

              {/* Instructions */}
              {canEdit && (
                <div className="text-center">
                  <p className="text-xs text-muted-foreground">
                    {isDragOver ? t('logo_ext.hint_drop') : t('logo_ext.hint_drag')}
                  </p>
                  <p className="text-[10px] text-muted-foreground mt-1">
                    {t('logo_ext.hint_specs')}
                  </p>
                </div>
              )}

              {/* Hidden file input */}
              <input
                ref={fileInputRef}
                type="file"
                accept="image/png,image/jpeg,image/webp,image/svg+xml"
                className="hidden"
                onChange={handleFileChange}
                disabled={uploading || !canEdit}
              />
            </div>

            {/* Actions */}
            {canEdit && (
              <div className="flex gap-2 mt-3">
                {team?.logo_url ? (
                  <>
                    <Button
                      variant="outline"
                      size="sm"
                      className="flex-1"
                      onClick={(e) => {
                        e.stopPropagation();
                        fileInputRef.current?.click();
                      }}
                      disabled={uploading}
                    >
                      <Camera className="w-3 h-3 mr-1" />
                      {t('logo_ext.change')}
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={(e) => {
                        e.stopPropagation();
                        setShowUrlInput(true);
                      }}
                      disabled={uploading}
                      title={t('logo_ext.url_mode_title')}
                    >
                      <Link2 className="w-3 h-3" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      className="text-destructive hover:text-destructive hover:bg-destructive/10"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleDelete();
                      }}
                      disabled={uploading}
                    >
                      <Trash2 className="w-3 h-3" />
                    </Button>
                  </>
                ) : (
                  <Button
                    variant="ghost"
                    size="sm"
                    className="w-full"
                    onClick={(e) => {
                      e.stopPropagation();
                      setShowUrlInput(true);
                    }}
                    disabled={uploading}
                  >
                    <Link2 className="w-3 h-3 mr-1" />
                    {t('logo_ext.url_mode_open')}
                  </Button>
                )}
              </div>
            )}
          </>
        )}
      </CardContent>
    </Card>
  );
}
